// Neighborhood insurance results (Oct 27, 2026). Three views of the same
// numbers (src/lib/neighborhoodClaims.ts):
//   NeighborhoodInsuranceLine  one line under a QC Board reinspection card;
//   NeighborhoodInsuranceCard  the deal page, for claims and reinspections;
//   PinNeighborhoodInsurance   the canvas pin sheet, for reps at the door.
import { useEffect, useState } from "react";
import {
  afterText,
  approvedFirst,
  beforeText,
  decidedFirst,
  fetchDealNeighborhoodOutcomes,
  fetchPointNeighborhoodOutcomes,
  isThin,
  pct,
  radiusLabel,
  type CarrierOutcomes,
  type DealNeighborhoodOutcomes,
  type PointNeighborhoodOutcomes,
} from "../../lib/neighborhoodClaims";

function ThinChip({ o }: { o: CarrierOutcomes }) {
  if (!isThin(o) || decidedFirst(o) === 0) return null;
  return (
    <span
      title="Fewer than 5 claims decided nearby. Read it as a hint, not a rate."
      className="rounded border border-slate-200 bg-slate-50 px-1 py-px text-[9px] font-semibold uppercase tracking-wide text-slate-400"
    >
      few claims
    </span>
  );
}

function RateChip({ o }: { o: CarrierOutcomes }) {
  const p = pct(approvedFirst(o), decidedFirst(o));
  if (p == null) return null;
  const tone = isThin(o)
    ? "bg-slate-100 text-slate-500 border-slate-200"
    : p >= 50
      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
      : p >= 25
        ? "bg-amber-50 text-amber-800 border-amber-200"
        : "bg-rose-50 text-rose-700 border-rose-200";
  return <span className={`rounded border px-1.5 py-0.5 font-semibold ${tone}`}>{p}% approved first</span>;
}

// ── QC Board reinspection card line ─────────────────────────────────────

export function NeighborhoodInsuranceLine({ outcome }: { outcome: DealNeighborhoodOutcomes | undefined }) {
  if (!outcome) return null;
  if (!outcome.carrier || outcome.no_location) return null;
  const before = beforeText(outcome);
  const after = afterText(outcome);
  const where = `within ${radiusLabel(outcome.radius_m)}`;
  return (
    <div className="mt-1.5 border-l-2 border-sky-200 pl-2">
      <div className="flex flex-wrap items-center gap-1.5 text-[10px]">
        <span className="font-semibold uppercase tracking-wide text-slate-400">Neighborhood</span>
        <span className="text-slate-600">
          {outcome.carrier} {where}
        </span>
        <RateChip o={outcome} />
        <ThinChip o={outcome} />
      </div>
      <div className={`mt-0.5 text-[11px] ${isThin(outcome) ? "text-slate-400" : "text-slate-600"}`}>
        {before || after
          ? [before, after].filter(Boolean).join(" · ")
          : `No other ${outcome.carrier} claims decided ${where}.`}
      </div>
    </div>
  );
}

// ── Deal page card ──────────────────────────────────────────────────────

export function NeighborhoodInsuranceCard({
  dealId,
  lat,
  lon,
}: {
  dealId: string;
  lat: number | null | undefined;
  lon: number | null | undefined;
}) {
  const [own, setOwn] = useState<DealNeighborhoodOutcomes | null>(null);
  const [area, setArea] = useState<PointNeighborhoodOutcomes | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const hasPoint = lat != null && lon != null && Number.isFinite(Number(lat)) && Number.isFinite(Number(lon));
  const others = (area?.carriers ?? []).filter((c) => c.carrier !== own?.carrier && decidedFirst(c) > 0);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setErr(null);
    Promise.all([
      fetchDealNeighborhoodOutcomes([dealId]),
      hasPoint ? fetchPointNeighborhoodOutcomes(Number(lat), Number(lon), dealId) : Promise.resolve(null),
    ])
      .then(([byDeal, pt]) => {
        if (!alive) return;
        setOwn(byDeal[dealId] ?? null);
        setArea(pt);
      })
      .catch((e: unknown) => {
        if (alive) setErr(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [dealId, lat, lon, hasPoint]);

  return (
    <div className="rounded-xl border border-sky-200 bg-white">
      <div className="flex items-center justify-between border-b border-sky-100 bg-sky-50/60 px-4 py-2">
        <div className="text-sm font-semibold text-bardin-ink">🏘 Neighborhood insurance results</div>
        {own && !own.no_location && hasPoint ? (
          <span className="text-[11px] text-slate-500">
            within {radiusLabel(own.radius_m)}
            {own.widened ? " (widened: few claims within 1 mi)" : ""}
          </span>
        ) : null}
      </div>
      <div className="space-y-3 px-4 py-3 text-sm">
        {loading ? (
          <div className="text-xs text-slate-400">Loading nearby claims…</div>
        ) : err ? (
          <div className="text-xs text-rose-600">Couldn&apos;t load nearby claims — {err}</div>
        ) : !hasPoint || own?.no_location ? (
          <div className="text-xs text-slate-500">No map location on this deal, so nearby claims can&apos;t be found. Re-save the address to fix it.</div>
        ) : (
          <>
            {own?.carrier ? (
              <div>
                <div className="flex flex-wrap items-center gap-1.5 text-xs">
                  <span className="font-semibold text-bardin-ink">{own.carrier}</span>
                  <RateChip o={own} />
                  <ThinChip o={own} />
                </div>
                <div className={`mt-1 space-y-0.5 text-xs ${isThin(own) ? "text-slate-400" : "text-slate-700"}`}>
                  <div>
                    <span className="font-semibold">Before:</span>{" "}
                    {beforeText(own) ?? `no other ${own.carrier} claims decided nearby yet`}
                  </div>
                  <div>
                    <span className="font-semibold">After:</span>{" "}
                    {afterText(own) ?? `no ${own.carrier} denials nearby to reinspect`}
                  </div>
                  {own.pending ? <div className="text-slate-400">{own.pending} more waiting on the carrier.</div> : null}
                </div>
              </div>
            ) : (
              <div className="text-xs text-amber-700">
                No insurance company on this deal. Add it to compare with nearby claims.
              </div>
            )}

            {others.length > 0 && area ? (
              <div>
                <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  Other carriers within {radiusLabel(area.radius_m)}
                </div>
                <CarrierTable carriers={others} />
              </div>
            ) : null}

            <div className="text-[10px] text-slate-400">
              Before = approved at the first adjuster meeting (partials count). After = first-meeting denials later
              won on reinspection. Filed claims only; claims with no carrier on record are left out
              {area?.unrecorded ? ` (${area.unrecorded} nearby)` : ""}.
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function CarrierTable({ carriers, max }: { carriers: CarrierOutcomes[]; max?: number }) {
  const rows = max ? carriers.slice(0, max) : carriers;
  return (
    <table className="w-full text-xs">
      <thead>
        <tr className="text-left text-[10px] uppercase tracking-wide text-slate-400">
          <th className="py-0.5 pr-2 font-semibold">Carrier</th>
          <th className="py-0.5 pr-2 text-right font-semibold">Approved first</th>
          <th className="py-0.5 text-right font-semibold">Won on reinsp.</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((c) => {
          const d = decidedFirst(c);
          const thin = isThin(c);
          return (
            <tr key={c.carrier ?? ""} className={`border-t border-slate-100 ${thin ? "text-slate-400" : "text-slate-700"}`}>
              <td className="py-1 pr-2 font-medium">{c.carrier}</td>
              <td className="py-1 pr-2 text-right tabular-nums">
                {d ? `${approvedFirst(c)} of ${d}` : "—"}
                {d && !thin ? <span className="ml-1 text-slate-400">({pct(approvedFirst(c), d)}%)</span> : null}
              </td>
              <td className="py-1 text-right tabular-nums">
                {c.denied_first ? `${c.won_on_reinspection} of ${c.denied_first}` : "—"}
                {c.reinspection_open ? <span className="ml-1 text-slate-400">+{c.reinspection_open} open</span> : null}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// ── Canvas pin sheet ────────────────────────────────────────────────────

// One fetch per house per page load: reps tap back and forth between pins.
const pinCache = new Map<string, PointNeighborhoodOutcomes>();

export function PinNeighborhoodInsurance({
  lat,
  lon,
  excludeDealId,
}: {
  lat: number | string;
  lon: number | string;
  excludeDealId: string | null;
}) {
  // knock_pins.lat/lon are numeric columns; they can arrive as strings.
  const la = Number(lat);
  const lo = Number(lon);
  const ok = Number.isFinite(la) && Number.isFinite(lo);
  const key = ok ? `${la.toFixed(5)},${lo.toFixed(5)},${excludeDealId ?? ""}` : "";
  const [data, setData] = useState<PointNeighborhoodOutcomes | null>(() => pinCache.get(key) ?? null);
  const [err, setErr] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    setShowAll(false);
    if (!ok) return;
    const hit = pinCache.get(key);
    if (hit) {
      setData(hit);
      setErr(null);
      return;
    }
    let alive = true;
    setData(null);
    setErr(null);
    fetchPointNeighborhoodOutcomes(la, lo, excludeDealId)
      .then((d) => {
        pinCache.set(key, d);
        if (alive) setData(d);
      })
      .catch((e: unknown) => {
        if (alive) setErr(e instanceof Error ? e.message : String(e));
      });
    return () => {
      alive = false;
    };
  }, [key, ok, la, lo, excludeDealId]);

  if (!ok) return null;
  const decided = data?.carriers.filter((c) => decidedFirst(c) > 0) ?? [];

  return (
    <div className="mt-3 rounded-md border border-sky-200 bg-sky-50/50 px-3 py-2">
      <div className="flex items-baseline justify-between">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-sky-800">
          🏘 Insurance results nearby
        </div>
        {data ? <div className="text-[10px] text-slate-500">within {radiusLabel(data.radius_m)}</div> : null}
      </div>
      {err ? (
        <div className="mt-1 text-xs text-rose-600">Couldn&apos;t load — {err}</div>
      ) : !data ? (
        <div className="mt-1 text-xs text-slate-400">Loading…</div>
      ) : decided.length === 0 ? (
        <div className="mt-1 text-xs text-slate-500">No decided claims nearby yet.</div>
      ) : (
        <>
          <div className="mt-1">
            <CarrierTable carriers={decided} max={showAll ? undefined : 4} />
          </div>
          {decided.length > 4 ? (
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              className="mt-1 text-[11px] font-semibold text-sky-700 hover:underline"
            >
              {showAll ? "Show fewer" : `Show all ${decided.length} carriers`}
            </button>
          ) : null}
          <div className="mt-1 text-[10px] text-slate-400">
            Approved first = at the first adjuster meeting. Grey = fewer than 5 decided.
          </div>
        </>
      )}
    </div>
  );
}
